import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Rating, TextField, Typography, useTheme,
} from '@mui/material';
import useStore from '../store';

export default function RatingDialog({ show, onClose }) {
  const theme = useTheme();
  const userRatings = useStore(s => s.userRatings);
  const setUserRating = useStore(s => s.setUserRating);
  const flushNow = useStore(s => s.flushNow);

  const existing = userRatings[String(show.id)] ?? {};

  const [rating, setRating] = useState(existing.rating ?? 0);
  const [notes, setNotes] = useState(existing.notes ?? '');

  const handleSave = () => {
    setUserRating(show.id, { ...show, rating, notes, dismissed: false });
    flushNow();
    if (show.onSave) show.onSave();
    onClose();
  };

  const handleDismiss = () => {
    setUserRating(show.id, { rating: 0, notes: '', dismissed: true, ...show });
    if (show.onSave) show.onSave();
    onClose();
  };

  const handleAddToWatchlist = () => {
    setUserRating(show.id, { rating: 0, notes: '', dismissed: false, ...show });
    if (show.onSave) show.onSave();
    onClose();
  };

  const isRecommendation = show.score !== undefined;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', pb: 0 }}>
        {show.title}
      </DialogTitle>

      <DialogContent>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {show.year}{show.seasons ? ` · ${show.seasons} season${show.seasons !== 1 ? 's' : ''}` : ''}
        </Typography>

        {isRecommendation && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'rgba(232,184,109,0.05)', borderRadius: 1, border: '1px solid rgba(232,184,109,0.1)' }}>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, display: 'block' }}>
              Butler Match: {show.score}/10
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              {show.reason}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary' }}>Your rating</Typography>
          <Rating
            value={rating}
            onChange={(_, v) => setRating(v ?? 0)}
            precision={0.5}
            size="large"
            sx={{ '& .MuiRating-iconFilled': { color: theme.palette.primary.main } }}
          />
        </Box>

        <TextField
          label="Notes"
          placeholder="What did you think?"
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={e => setNotes(e.target.value)}
          size="small"
          variant="outlined"
        />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Button onClick={handleDismiss} color="error" size="small" sx={{ textTransform: 'none' }}>
            Not interested
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} color="inherit" size="small">Cancel</Button>
          {isRecommendation && (
            <Button onClick={handleAddToWatchlist} variant="outlined" size="small" sx={{ textTransform: 'none' }}>
              Queue it
            </Button>
          )}
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={rating === 0}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
